import React, { useEffect, useState, useCallback } from "react";
import './NavBar.css';
import { Typography, Button, Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import brandingLogo from "../../../img/nebraskaBrandingLogo.png";
import { getB2CRedirectLogoutUrl } from "../../../utils/UrlConstants";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useMsal } from "@azure/msal-react";
import { setLogoutInProgress, getStoredToken, getCurrentUserData } from "../../../utils/authUtils";

const NavBar: React.FunctionComponent = () => {
    const [externalTokenName, setExternalTokenName] = useState<string | null>(null);
    const [internalTokenName, setInternalTokenName] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();

    // Function to update user names from tokens
    const updateUserNames = useCallback(() => {
        // Check for external token
        const externalToken = getStoredToken();
        if (externalToken) {
            // Try multiple name fields from the token
            let name = externalToken.name ||
                externalToken.given_name ||
                externalToken.displayName ||
                externalToken.unique_name ||
                externalToken.preferred_username;

            // If no name in token, try to get from user data
            if (!name) {
                const userData = getCurrentUserData();
                if (userData) {
                    name = userData.firstName && userData.lastName
                        ? `${userData.firstName} ${userData.lastName}`
                        : userData.firstName || userData.lastName || userData.email || userData.name;
                }
            }

            // Fallback to email if still no name
            if (!name && externalToken.email) {
                name = externalToken.email;
            }

            // Final fallback
            name = name || 'External User';

            setExternalTokenName(name);
            setInternalTokenName(null); // Clear internal token if external exists
            console.log('NavBar: Updated external user name to:', name);
            return;
        }

        // Check for internal token
        const internalToken = sessionStorage.getItem("internal_token");
        if (internalToken && accounts.length > 0) {
            const name = accounts[0]?.name ?? 'Internal User';
            setInternalTokenName(name);
            setExternalTokenName(null); // Clear external token if internal exists
            console.log('NavBar: Updated internal user name to:', name);
            return;
        }

        // Clear both if no tokens found
        console.log('NavBar: No tokens found, clearing user names');
        setExternalTokenName(null);
        setInternalTokenName(null);
    }, [accounts]);

    // Initial load and when accounts change
    useEffect(() => {
        updateUserNames();
    }, [updateUserNames]);

    // Listen for storage changes (when tokens are added/removed)
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'external_token' || e.key === 'internal_token') {
                console.log('Token storage changed, updating NavBar user display');
                updateUserNames();
            }
        };

        // Also listen for custom events when session storage is updated programmatically
        const handleCustomStorageChange = () => {
            updateUserNames();
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('sessionStorageUpdate', handleCustomStorageChange);

        // Set up interval to periodically check for token changes
        // Start with more frequent checks, then reduce frequency
        let checkCount = 0;
        let longIntervalRef: NodeJS.Timeout | null = null;

        const interval = setInterval(() => {
            updateUserNames();
            checkCount++;
            // After 10 checks (10 seconds), clear the interval to reduce overhead
            if (checkCount >= 10) {
                clearInterval(interval);
                // Set up a less frequent check
                longIntervalRef = setInterval(updateUserNames, 3000);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('sessionStorageUpdate', handleCustomStorageChange);
            clearInterval(interval);
            if (longIntervalRef) {
                clearInterval(longIntervalRef);
            }
        };
    }, [updateUserNames]);

    const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDropdownClose = () => {
        setAnchorEl(null);
    };

    const handleMyAccountClick = () => {
        navigate('/my-account');
        handleDropdownClose();
    };

    const handleLogoutClick = () => {
        // Set logout in progress flag
        setLogoutInProgress(true);

        if (internalTokenName) {
            sessionStorage.removeItem("internal_token");
            sessionStorage.removeItem("internal_groups");
            sessionStorage.removeItem("msalToken");
            sessionStorage.removeItem("currentUserData");
            window.dispatchEvent(new Event('sessionStorageUpdate'));
            instance.logoutRedirect({
                account: accounts[0],
                postLogoutRedirectUri: "/",
            });
        } else {
            // Logout for external token - remove from session storage
            sessionStorage.removeItem("external_token");
            sessionStorage.removeItem("external_groups");
            sessionStorage.removeItem("msalToken");
            sessionStorage.removeItem("currentUserData");
            window.dispatchEvent(new Event('sessionStorageUpdate'));
            window.location.href = getB2CRedirectLogoutUrl();
        }
    };

    return (
        <div className="navbar-container">
            <nav className="navbar">
                <div className="navbar-left">
                    <img
                        src={brandingLogo}
                        alt="Nebraska Dept. of Health and Human Services"
                        className="logo-image"
                        onClick={() => window.location.href = '/'}
                    />
                    <div className="vertical-line"></div>
                    <div className="navbar-title">
                        <h3 style={{
                            color: 'white',
                            margin: 0,
                            fontSize: '1.25rem',
                            fontWeight: '500',
                            letterSpacing: '0.5px'
                        }}>
                            Common Identity Access and Authorization Management
                        </h3>
                    </div>
                </div>
                <div className="navbar-right">
                    {(internalTokenName || externalTokenName) && (
                        <div onMouseEnter={handleDropdownOpen} onMouseLeave={handleDropdownClose}>
                            <Button
                                className="nav-button"
                                style={{
                                    fontFamily: 'inherit',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    textTransform: 'none',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {`Welcome: ${internalTokenName || externalTokenName}`}
                                <ArrowDropDownIcon style={{ marginLeft: '5px' }} />
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleDropdownClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                            >
                                {externalTokenName && (
                                    <MenuItem
                                        onClick={handleMyAccountClick}
                                        sx={{
                                            fontSize: '1rem',
                                            color: 'black',
                                            '&:hover': {
                                                backgroundColor: '#003b61',
                                                color: 'white',
                                            }
                                        }}
                                    >
                                        My Account
                                    </MenuItem>
                                )}
                                <MenuItem
                                    onClick={handleLogoutClick}
                                    sx={{
                                        fontSize: '1rem',
                                        color: 'black',
                                        '&:hover': {
                                            backgroundColor: '#003b61',
                                            color: 'white',
                                        }
                                    }}
                                >
                                    Logout
                                </MenuItem>
                            </Menu>
                        </div>
                    )}
                </div>
            </nav>
            <div className="navbar-footer"></div>
        </div>

    );
};

export default NavBar;
